import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import Badge from './Badge';
import { ChevronDownIcon } from './Icons';
import { resolveStatusTextColor, type StatusTextColorMode } from '../../utils/statusColor';

interface StatusBadgeStylePickerProps {
  backgroundColor?: string;
  textColorMode?: StatusTextColorMode;
  onChange: (backgroundColor: string, textColorMode: StatusTextColorMode) => void;
  label?: string;
  previewLabel?: string;
  error?: string;
  helperText?: string;
}

const TEXT_MODE_OPTIONS: Array<{ value: StatusTextColorMode; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'dark', label: 'Dark text' },
  { value: 'light', label: 'Light text' },
];

const PRESET_COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#64748b'];

const StatusBadgeStylePicker: React.FC<StatusBadgeStylePickerProps> = ({
  backgroundColor = '#64748b',
  textColorMode = 'auto',
  onChange,
  previewLabel = 'Preview',
  error,
  helperText,
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const resolvedTextColor = resolveStatusTextColor(backgroundColor, textColorMode);
  const previewStyle = {
    backgroundColor,
    borderColor: backgroundColor,
    color: resolvedTextColor,
  };
  const helperMessage = error || helperText;

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) {
      setDropdownRect(null);
      return;
    }

    const updateRect = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom + 6,
        left: rect.left,
        width: Math.min(360, Math.max(rect.width, 300)),
      });
    };

    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-1.5">
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className={cn(
            'w-full rounded-lg border h-10 px-3 py-1.5 text-sm transition-all flex items-center justify-between gap-3',
            colors.surface,
            colors.border,
            isOpen && 'ring-2 shadow-sm',
            isOpen && colors.primaryBorder,
            !isOpen && colors.interactiveHover,
            error && 'border-red-500',
          )}
        >
          <div className="min-w-0 flex items-center gap-2">
            <Badge
              variant="secondary"
              className="border rounded-full px-2.5 py-0.5"
              style={previewStyle}
            >
              {previewLabel}
            </Badge>
          </div>
          <ChevronDownIcon
            className={cn(
              'w-4 h-4 flex-shrink-0 transition-transform',
              colors.textMuted,
              isOpen && 'rotate-180',
            )}
          />
        </button>

        {isOpen && dropdownRect && createPortal(
          <div
            ref={dropdownRef}
            className={cn(
              'fixed z-[9999] rounded-xl border shadow-xl p-3 space-y-3',
              colors.surface,
              colors.border,
            )}
            style={{
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <Badge
                variant="secondary"
                className="border rounded-full px-2.5 py-0.5"
                style={previewStyle}
              >
                {previewLabel}
              </Badge>
              <div className={cn('text-xs text-right', colors.textMuted)}>
                <div>{backgroundColor.toUpperCase()}</div>
                <div>{textColorMode.toUpperCase()} TEXT</div>
              </div>
            </div>

            <div className="space-y-2">
              <div className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                Background
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {PRESET_COLORS.map((preset) => {
                  const isActive = preset.toLowerCase() === backgroundColor.toLowerCase();
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => onChange(preset, textColorMode)}
                      className={cn(
                        'h-7 w-7 rounded-full border-2 transition-all',
                        isActive
                          ? cn(colors.primaryBorderAll, 'ring-2 scale-110')
                          : 'border-black/10 hover:scale-105',
                      )}
                      style={{ backgroundColor: preset }}
                      title={preset}
                    />
                  );
                })}
                <button
                  type="button"
                  onClick={() => colorInputRef.current?.click()}
                  className={cn(
                    'h-7 px-2 rounded-md border text-xs font-medium transition-colors',
                    colors.surfaceHover,
                    colors.border,
                    colors.text,
                    colors.interactiveHover,
                  )}
                >
                  Custom
                </button>
                <input
                  ref={colorInputRef}
                  type="color"
                  value={backgroundColor}
                  onChange={(e) => onChange(e.target.value, textColorMode)}
                  className="sr-only"
                />
                <span className={cn('text-xs ml-auto', colors.textMuted)}>
                  {backgroundColor.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                Text
              </div>
              <div className="grid grid-cols-3 gap-2">
                {TEXT_MODE_OPTIONS.map((option) => {
                  const isActive = option.value === textColorMode;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onChange(backgroundColor, option.value)}
                      className={cn(
                        'rounded-lg border px-2 py-2 text-left transition-all',
                        isActive
                          ? cn(colors.primaryBorderAll, colors.surfaceHover, 'ring-2 ring-offset-0')
                          : cn(colors.border, colors.surface, colors.interactiveHover),
                      )}
                    >
                      <div className={cn('text-xs font-semibold', colors.text)}>{option.label}</div>
                      <div className="mt-1">
                        <Badge
                          variant="secondary"
                          className="border rounded-full px-2.5 py-0.5"
                          style={{
                            backgroundColor,
                            borderColor: backgroundColor,
                            color: resolveStatusTextColor(backgroundColor, option.value),
                          }}
                        >
                          Ab
                        </Badge>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
      </div>

      {helperMessage && (
        <div className={cn('text-sm', error ? colors.error : colors.textMuted)}>
          {helperMessage}
        </div>
      )}
    </div>
  );
};

export default StatusBadgeStylePicker;
